import React, { useEffect } from 'react';

import { AppStateContext } from 'src/contexts/app-state-provider.context';
import Footer from './footer';
import Helmet from 'react-helmet';
import MainHeader from './main-header';
import PropTypes from 'prop-types';
import { BackToTopBtn } from './back-to-top-btn';
import SSRPage from 'src/components/ssr-page';

const isBrowser = typeof window !== 'undefined';

export default function Layout({ title, showNavbar, _rpgCode, className, children, showBackToTop = true }) {
  // Initialize GA4 event listeners
  useEffect(() => {
    if (!isBrowser) return;

    // Wait a moment to ensure GA4 script is loaded
    setTimeout(() => {
      if (window.gtag) {
        console.log('✅ GA4 gtag is available and ready for event tracking');
      } else {
        console.warn('⚠️ GA4 gtag not available - check if script loaded properly');
      }
    }, 1000);

    // Global download tracking for any PDF links not caught by onClick handlers
    const trackPDFDownloads = (e) => {
      // Skip if this event was already tracked by a specific onClick handler
      if (e.__ga4Tracked) {
        console.log('⏭️ Skipping global tracking - already tracked by onClick handler');
        return;
      }
      
      const link = e.target.closest('a');
      if (link && link.href && link.href.includes('.pdf')) {
        const filePath = link.pathname;  // e.g. /pdf/NZ_Amsler-grid-downloadable-resource_M-NZ-00001378_v1_AUG2025.pdf — matches GA4 Enhanced Measurement format
        console.log('🌍 Global PDF Download Detected (untracked link):', filePath);
        
        // GA4 tracking - only for PDFs without specific tracking
        if (window.gtag) {
          window.gtag('event', 'file_download_untracked', {
            file_name: filePath,
            file_extension: 'pdf',
            link_url: link.href
          });
          console.log('📊 Global GA4 Event Sent:', filePath);
        }
        
        // Adform tracking
        if (window._adftrack) {
          const adformData = {
            HttpHost: 'asia.adform.net',
            pm: 3214201,
            divider: encodeURIComponent('|'),
            pagename: encodeURIComponent('download'),
            custom: {
              'file_name': filePath,
              'file_type': 'pdf',
              'download_action': 'global_pdf_download'
            }
          };
          window._adftrack.push(adformData);
          console.log('🎯 Global Adform Event Sent:', adformData);
        }
      }
    };

    // Add global event listener for PDF downloads
    if (isBrowser) {
      document.addEventListener('click', trackPDFDownloads);
      return () => document.removeEventListener('click', trackPDFDownloads);
    }
  }, []);

  if (!isBrowser){
    return <SSRPage />;
  }

  return (
    <div className={className}>
      <AppStateContext.Consumer>
        {({ language }) => (
          <>
            <Helmet title={title[language] + ' | Vabysmo NZ'} meta={[{ name: 'robots', content: 'index,follow' }]}>
              <html lang='en-nz'/>

              {/* Google Analytics 4 Script */}
              <script async src="https://www.googletagmanager.com/gtag/js?id=G-EQVB92CVWD"></script>
              <script type="text/javascript">{`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-EQVB92CVWD', {
                  'debug_mode': true,
                  'anonymize_ip': false,
                  'allow_google_signals': true,
                  'allow_ad_personalization_signals': true
                });
                console.log('✅ GA4 gtag script loaded with demographics enabled');
              `}</script>

              <script
                src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
                data-language="en-NZ"
                type="text/javascript"
                charset="UTF-8"
                data-domain-script="5d0b62db-363a-48e2-9d72-c09d5036150b" >

              </script>
              <script type="text/javascript">{`function OptanonWrapper() {}`}</script>
              
              {/* Adform Tracking Code BEGIN - Enhanced Version */}
              <script type="text/javascript">{`
                window._adftrack = Array.isArray(window._adftrack) ? window._adftrack : (window._adftrack ? [window._adftrack] : []);
                window._adftrack.push({
                    HttpHost: 'asia.adform.net',
                    pm: 3214201
                });
                
                // Enhanced script loading with error handling
                (function () { 
                    var s = document.createElement('script'); 
                    s.type = 'text/javascript'; 
                    s.async = true; 
                    s.src = 'https://s2.adform.net/banners/scripts/st/trackpoint-async.js';
                    s.onload = function() {
                        console.log('✅ Adform trackpoint-async.js loaded successfully');
                    };
                    s.onerror = function() {
                        console.log('⚠️ Adform script failed to load, using fallback pixel');
                        // Fallback: Direct pixel fire
                        var img = new Image();
                        img.src = 'https://asia.adform.net/Serving/TrackPoint/?pm=3214201&rnd=' + Math.random();
                    };
                    var x = document.getElementsByTagName('script')[0]; 
                    x.parentNode.insertBefore(s, x); 
                })();
              `}</script>
              {/* Adform Tracking Code END */}

              <body />
            </Helmet>
            {/* Adform noscript fallback - outside Helmet */}
            <noscript>
              <p style={{margin:0, padding:0, border:0}}>
                <img src="https://asia.adform.net/Serving/TrackPoint/?pm=3214201" width="1" height="1" alt="" />
              </p>
            </noscript>
            <MainHeader siteTitle={title[language] + ' | Vabysmo'} showNavbar={showNavbar} />
          </>
        )}
      </AppStateContext.Consumer>
      <main id="main-content">
        {children}
        {showBackToTop ? <BackToTopBtn /> : ''}
      </main>
      <Footer />
    </div>
  );
}

Layout.propTypes = {
  title: PropTypes.object.isRequired,
  showNavbar: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node.isRequired
};

Layout.defaultProps = {
  showNavbar: false
};
